"use client";

import { useState } from "react";
import { suggestBabyNames } from "@/lib/api/baby-names";
import type { BabyNameResponse } from "@/lib/api/baby-names";
import type { KundaliRequest } from "@ishvaram/api-client";
import CalculatorCrossLinks from "@/components/CalculatorCrossLinks";
import { trackCalculatorUsed } from "@/lib/analytics";
import { BabyNamesForm, BabyNamesResult } from "@/components/baby-names";

export default function BabyNamesClient() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BabyNameResponse | null>(null);
  const [genderFilter, setGenderFilter] = useState<string>("all");

  async function handleBirthSubmit(data: KundaliRequest) {
    setLoading(true);
    setError(null);
    try {
      const response = await suggestBabyNames({
        birth_date: data.birth_date,
        birth_time: data.birth_time ?? null,
        birth_place: data.birth_place,
        gender: genderFilter !== "all" ? genderFilter : undefined,
        limit: 200,
      });
      setResult(response);
      trackCalculatorUsed({ calculator: "baby-names" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  async function handleNakshatraSubmit(data: {
    nakshatra: string;
    pada?: number;
    birthDate: string;
  }) {
    if (!data.nakshatra || !data.birthDate) {
      setError("Please select a nakshatra and enter birth date.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await suggestBabyNames({
        birth_date: data.birthDate,
        nakshatra: data.nakshatra,
        pada: data.pada,
        gender: genderFilter !== "all" ? genderFilter : undefined,
        limit: 200,
      });
      setResult(response);
      trackCalculatorUsed({ calculator: "baby-names" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12 sm:py-16">
        {/* Header */}
        <div className="text-center mb-10">
          <p className="text-[11px] font-semibold uppercase tracking-[0.25em] mb-3 text-ish-accent">
            Naam Karan
          </p>
          <h1 className="text-[28px] sm:text-[36px] font-bold leading-tight font-serif text-ish-dark">
            Baby Names by Nakshatra
          </h1>
          <p className="mt-3 text-[15px] leading-relaxed max-w-md mx-auto text-ish-text-muted">
            Find the perfect name for your child &mdash; filtered by birth star and scored by
            numerology compatibility.
          </p>

          {/* Trust signals */}
          <div className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2 text-[11px] font-medium text-ish-text-faint">
            <span className="flex items-center gap-1.5">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              1500+ Names
            </span>
            <span className="flex items-center gap-1.5">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
              Instant Results
            </span>
            <span className="flex items-center gap-1.5">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              100% Free
            </span>
          </div>
        </div>

        {/* Input Section */}
        {!result ? (
          <BabyNamesForm
            loading={loading}
            error={error}
            onBirthSubmit={handleBirthSubmit}
            onNakshatraSubmit={handleNakshatraSubmit}
            genderFilter={genderFilter}
            onGenderFilterChange={setGenderFilter}
          />
        ) : (
          <BabyNamesResult
            result={result}
            onReset={() => {
              setResult(null);
              setError(null);
            }}
          />
        )}

        {/* Cross Links */}
        <CalculatorCrossLinks currentPage="baby-names" />
      </div>
    </div>
  );
}
